import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import CheckoutShell from '@/components/checkout/CheckoutShell';
import CheckoutAlert from '@/components/checkout/CheckoutAlert';
import CheckoutConfirmation from '@/components/checkout/CheckoutConfirmation';
import OrderSummaryCard from '@/components/checkout/OrderSummaryCard';
import { buttonClass } from '@/components/checkout/CheckoutControls';
import LoginStep from '@/components/checkout/steps/LoginStep';
import ContactInformationStep from '@/components/checkout/steps/ContactInformationStep';
import AcceleratorStep from '@/components/checkout/steps/AcceleratorStep';
import PaymentStep from '@/components/checkout/steps/PaymentStep';
import CompletePurchaseStep, {
  type PurchaseStatus,
} from '@/components/checkout/steps/CompletePurchaseStep';
import { buildOrderSummary, getCheckoutPlan } from '@/lib/checkout/pricing';
import { clearDraft, loadDraft, saveDraft } from '@/lib/checkout/draftStorage';
import { confirmAuthenticationChallenge, submitPayment } from '@/lib/checkout/services';
import { CHECKOUT_STEPS, emptyDraft, type CheckoutDraft, type CheckoutStepId } from '@/lib/checkout/types';

const STEP_LABEL: Record<CheckoutStepId, string> = {
  login: 'Create your login',
  contact: 'Contact information',
  accelerator: 'Add the Accelerator course',
  payment: 'Payment',
  complete: 'Complete membership purchase',
};

interface PaymentFailure {
  title: string;
  message: string;
}

/**
 * Member checkout (AR-290) — five screens driven as one continuous session.
 *
 * Progress is persisted after every change so an interrupted checkout resumes on
 * the step the member left. Provisioning, USPS and Stripe are stubbed in
 * `@/lib/checkout/services` for MVP.
 */
export default function Checkout() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const planId = new URLSearchParams(search).get('plan');

  const [draft, setDraft] = useState<CheckoutDraft>(() => emptyDraft());
  const [resumed, setResumed] = useState<CheckoutDraft | null>(null);
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<PurchaseStatus>('idle');
  const [paymentFailure, setPaymentFailure] = useState<PaymentFailure | null>(null);
  const [purchased, setPurchased] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const plan = useMemo(() => getCheckoutPlan(planId), [planId]);
  const order = useMemo(
    () =>
      buildOrderSummary({
        plan,
        acceleratorAdded: draft.accelerator.added,
        couponCode: draft.appliedCoupon,
      }),
    [plan, draft.accelerator.added, draft.appliedCoupon],
  );

  // Restore an interrupted checkout (refresh, session timeout, closed tab).
  useEffect(() => {
    const stored = loadDraft();
    if (!stored) return;
    setDraft(stored);
    if (stored.step !== 'login') setResumed(stored);
  }, []);

  // Persist after every change so nothing is lost mid-session.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (purchased) return;
    saveDraft(draft);
  }, [draft, purchased]);

  // Move focus to the step announcement on every step change, but not on load.
  const previousStep = useRef<CheckoutStepId>(draft.step);
  useEffect(() => {
    if (previousStep.current !== draft.step) {
      previousStep.current = draft.step;
      headingRef.current?.focus();
    }
  }, [draft.step]);

  const patch = <K extends 'login' | 'contact' | 'accelerator' | 'payment' | 'agreement'>(
    key: K,
    value: Partial<CheckoutDraft[K]>,
  ) => setDraft((current) => ({ ...current, [key]: { ...current[key], ...value } }));

  const goToStep = (step: CheckoutStepId) => {
    setResumeDismissed(true);
    setDraft((current) => ({ ...current, step }));
  };

  const stepIndex = CHECKOUT_STEPS.indexOf(draft.step);
  const goNext = () => goToStep(CHECKOUT_STEPS[Math.min(stepIndex + 1, CHECKOUT_STEPS.length - 1)]);
  const goBack = () => goToStep(CHECKOUT_STEPS[Math.max(stepIndex - 1, 0)]);

  /** Declined / errored charges send them back to payment with their data intact. */
  const failPayment = (title: string, message: string) => {
    setPurchaseStatus('idle');
    setPaymentFailure({ title, message });
    goToStep('payment');
  };

  const completePurchase = async () => {
    setPurchaseStatus('submitting');

    let outcome: Awaited<ReturnType<typeof submitPayment>>;
    try {
      outcome = await submitPayment(draft.payment.cardNumber);
    } catch {
      failPayment(
        'We couldn’t reach the payment processor',
        'Something went wrong on the way to Stripe. Nothing has been charged.',
      );
      return;
    }

    if (outcome.status === 'succeeded') {
      setPurchaseStatus('idle');
      clearDraft();
      setPurchased(true);
      return;
    }
    if (outcome.status === 'requires_authentication') {
      setPurchaseStatus('authenticating');
      return;
    }
    if (outcome.status === 'provisioning_failed') {
      setPurchaseStatus('provisioning_failed');
      clearDraft();
      return;
    }

    failPayment(
      outcome.status === 'declined'
        ? 'Payment failed — please try again'
        : 'We couldn’t reach the payment processor',
      outcome.message,
    );
  };

  const confirmChallenge = async () => {
    setPurchaseStatus('submitting');
    try {
      await confirmAuthenticationChallenge();
    } catch {
      failPayment(
        'We couldn’t verify this payment',
        'Your bank didn’t complete the verification. Nothing has been charged.',
      );
      return;
    }
    setPurchaseStatus('idle');
    clearDraft();
    setPurchased(true);
  };

  const backToPlans = () => setLocation('/landing');

  const resumeBanner =
    resumed && !resumeDismissed ? (
      <CheckoutAlert
        tone="success"
        title={`Welcome back${resumed.login.firstName ? `, ${resumed.login.firstName}` : ''} — picking up where you left off`}
        actions={
          <>
            <button
              type="button"
              onClick={() => setResumeDismissed(true)}
              className={buttonClass('sm')}
            >
              Continue
            </button>
            <button
              type="button"
              onClick={() => goToStep('login')}
              className={buttonClass('smOutline')}
            >
              Review earlier steps
            </button>
            <button
              type="button"
              onClick={() => {
                clearDraft();
                setResumed(null);
                setResumeDismissed(true);
                setDraft(emptyDraft());
              }}
              className={buttonClass('smGhost')}
            >
              Start over
            </button>
          </>
        }
      >
        You stopped on step {CHECKOUT_STEPS.indexOf(resumed.step) + 1} of {CHECKOUT_STEPS.length}.
        Everything you entered is saved.
      </CheckoutAlert>
    ) : null;

  const summary = (
    <OrderSummaryCard
      plan={plan}
      order={order}
      appliedCoupon={draft.appliedCoupon}
      onApplyCoupon={(code) => setDraft((current) => ({ ...current, appliedCoupon: code }))}
      onRemoveCoupon={() => setDraft((current) => ({ ...current, appliedCoupon: '' }))}
    />
  );

  if (purchased) {
    return (
      <CheckoutShell step="complete" summary={summary} onBackToPlans={backToPlans}>
        <CheckoutConfirmation
          email={draft.login.email}
          order={order}
          onGoToSignIn={() => setLocation('/login')}
        />
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell
      step={draft.step}
      banner={resumeBanner}
      summary={summary}
      onBackToPlans={backToPlans}
    >
      {/* Focus target so each step change is announced. */}
      <div
        ref={headingRef}
        tabIndex={-1}
        className="sr-only"
      >{`Step ${stepIndex + 1} of ${CHECKOUT_STEPS.length}: ${STEP_LABEL[draft.step]}`}</div>

      {draft.step === 'login' ? (
        <LoginStep
          value={draft.login}
          onChange={(value) => patch('login', value)}
          onNext={goNext}
          onSignIn={() => setLocation('/login')}
        />
      ) : null}

      {draft.step === 'contact' ? (
        <ContactInformationStep
          value={draft.contact}
          onChange={(value) => patch('contact', value)}
          onNext={goNext}
          onBack={goBack}
        />
      ) : null}

      {draft.step === 'accelerator' ? (
        <AcceleratorStep
          value={draft.accelerator}
          onChange={(value) => patch('accelerator', value)}
          onNext={goNext}
          onBack={goBack}
          setupFeeCents={plan.setupFeeCents}
        />
      ) : null}

      {draft.step === 'payment' ? (
        <PaymentStep
          value={draft.payment}
          onChange={(value) => patch('payment', value)}
          onNext={goNext}
          onBack={goBack}
          failure={paymentFailure}
          onDismissFailure={() => setPaymentFailure(null)}
        />
      ) : null}

      {draft.step === 'complete' ? (
        <CompletePurchaseStep
          plan={plan}
          order={order}
          value={draft.agreement}
          onChange={(value) => patch('agreement', value)}
          onBack={goBack}
          onSubmit={() => void completePurchase()}
          onConfirmAuthentication={() => void confirmChallenge()}
          status={purchaseStatus}
        />
      ) : null}
    </CheckoutShell>
  );
}
